import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { Button, DefaultPopup, FileInput, Icon, Label } from '@atlas/ui';

const stepsList = [
  'atlas_transfer_step_one',
  'atlas_transfer_step_two',
  'atlas_transfer_step_three',
  'atlas_transfer_step_four',
];

const sendData = async (publicPaymentId, file) => {
  const formData = new FormData();
  formData.append('proofFile', file);

  const request = prepareRequest({
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    method: 'POST',
    url: `payment/v2/payments/${publicPaymentId}/proof`,
  });

  return request(formData);
};

const AtlasTransferPopup = ({ data, id }) => {
  const {
    openSecondStep = false,
    paymentName = 'atlas_transfer',
    publicPaymentId,
    redirectUrl = '',
  } = data;
  const urlData = qs.parse(redirectUrl);
  const [loadReceipt, setLoadReceipt] = useState(openSecondStep);
  const [file, setFile] = useState(null);
  const [isCopied, setIsCopied] = React.useState(false);

  const handleClose = useCallback(() => NotificationManager.removeNotification(id), [id]);

  const isButtonDisabled = loadReceipt && !file;

  const copy = useCallback(e => {
    e.preventDefault();
    const inp = document.getElementById('bankingDetail');
    inp.select();
    document.execCommand('copy', false);
    setIsCopied(true);
  }, []);

  const handleClick = useCallback(() => {
    if (loadReceipt) {
      sendData(publicPaymentId, file)
        .then(() => {
          handleClose();
          NotificationManager.notify({
            message: 'atlas_transfer_successfully',
            status: 'success',
            type: 'notification',
          });
        })
        .catch(() => {
          handleClose();
          NotificationManager.notify({
            message: 'atlas_transfer_error',
            status: 'error',
            type: 'notification',
          });
        });
    } else {
      setLoadReceipt(true);
    }
  }, [file, handleClose, loadReceipt, publicPaymentId]);

  return (
    <DefaultPopup
      className="deposit-atlas-transfer-popup"
      headerTitle={paymentName}
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <div className="popup__content">
        {loadReceipt ? (
          <div className="atlas-transfer-form">
            <Label className="atlas-transfer-form-title" message="atlas_transfer_second_title" />
            <FileInput
              description="atlas_transfer_file_description"
              label="atlas_transfer_file_label"
              name="atlas-transfer-file"
              onChange={setFile}
              value={file}
            />
          </div>
        ) : (
          <div className="atlas-transfer-info">
            <Label className="atlas-transfer-info-title" message="atlas_transfer_title" />
            <ol className="atlas-transfer-info-list">
              {stepsList.map((step, index) => (
                <li className="atlas-transfer-info-list-item" key={step}>
                  {index + 1}. <Label message={step} />
                </li>
              ))}
            </ol>
            <Label className="atlas-transfer-info-text" message="atlas_transfer_info" />
            <Label className="atlas-transfer-copy-title" message="atlas_transfer_copy_title" />
            <div className="atlas-transfer-copy-data">
              <textarea className="atlas-transfer-copy-text" id="bankingDetail">
                {urlData.bankingDetail}
              </textarea>
              <Button
                className="atlas-transfer-copy-button"
                data-testid="copy-button"
                onClick={copy}
                type="outline"
              >
                <Icon
                  className={isCopied && 'copied'}
                  height={24}
                  name={isCopied ? 'success' : 'copy'}
                  width={24}
                />
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="popup__footer">
        <Button
          className="atlas-transfer-button btn"
          disabled={isButtonDisabled}
          label="atlas_transfer_button_text"
          onClick={handleClick}
          size="big"
          type="primary"
        />
      </div>
    </DefaultPopup>
  );
};

AtlasTransferPopup.propTypes = {
  data: PropTypes.shape({
    openSecondStep: PropTypes.bool,
    paymentName: PropTypes.string,
    publicPaymentId: PropTypes.string,
    redirectUrl: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default AtlasTransferPopup;
